import React, { useEffect, useRef, useState } from "react";
import { subscribeOutbox, retrySave, removeSave, sendNow } from "../../utils/offlineOutbox";
import { useConfirm } from "../errors";
import "./OutboxStatus.css";

// The saves waiting for signal (utils/offlineOutbox.js), in the top bar:
// "3 waiting to send" — or "1 didn't send" — and, opened, each one with when
// it was made; one that didn't send says why, with Try again and Remove.
// Nothing shows while nothing waits — except "No signal" while a
// technician's kept session is open (AuthContext: opened with no signal; it
// signs in by itself when signal returns). A browser that can't store them
// (a private window) keeps them in the page only: it says so.

const timeOf = (ms) => new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const STATE_WORDS = { waiting: "Waiting for signal", sending: "Sending…", failed: "Didn't send" };

export default function OutboxStatus({ offline = false }) {
  const [saves, setSaves] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  useEffect(() => subscribeOutbox(setSaves), []);
  // Removing a save throws the change away for good: asked first.
  const askRemove = async (s) => {
    const ok = await confirm({
      title: "Remove this change?",
      message: `"${s.label || "A change"}" didn't send. Removing it throws it away: it never reaches Chariot.`,
      confirmText: "Remove",
      variant: "danger",
    });
    if (ok) removeSave(s.id);
  };
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  useEffect(() => { if (!saves.length) setOpen(false); }, [saves.length]);

  if (!saves.length) {
    return offline ? (
      <div className="obx">
        <span className="obx-pill" title="Working from what this device kept: Chariot signs in again by itself when there's signal">
          <span className="obx-dot" aria-hidden="true" />
          No signal
        </span>
      </div>
    ) : null;
  }
  const failed = saves.filter((s) => s.state === "failed").length;
  const waiting = saves.length - failed;
  const words = [waiting && `${waiting} waiting to send`, failed && `${failed} didn't send`].filter(Boolean).join(" · ");
  const inPageOnly = saves.some((s) => s.persisted === false);

  return (
    <div className="obx" ref={boxRef}>
      {confirmDialog}
      <button type="button" className={`obx-pill${failed ? " has-failed" : ""}`} onClick={() => setOpen((v) => !v)} aria-expanded={open} title="Saves made with no signal">
        <span className="obx-dot" aria-hidden="true" />
        {words}
      </button>
      {open && (
        <div className="obx-panel" role="dialog" aria-label="Saves waiting to send">
          <div className="obx-head">
            <span>
              {inPageOnly
                ? "This browser can't keep them: leave Chariot open here until they've gone."
                : "Kept on this device: they go by themselves when there's signal."}
            </span>
            {waiting > 0 && <button type="button" className="obx-link" onClick={() => sendNow()}>Send now</button>}
          </div>
          <ul className="obx-list">
            {saves.map((s) => (
              <li key={s.id} className={`obx-item is-${s.state}`}>
                <div className="obx-item-main">
                  <span className="obx-item-label">{s.label || "A change"}</span>
                  <span className="obx-item-meta">{STATE_WORDS[s.state] || STATE_WORDS.waiting} · made {timeOf(s.createdAt)}</span>
                  {s.error && <span className="obx-item-error">{s.error}</span>}
                </div>
                {s.state === "failed" && (
                  <div className="obx-item-actions">
                    <button type="button" className="obx-link" onClick={() => retrySave(s.id)}>Try again</button>
                    <button type="button" className="obx-link obx-link-danger" onClick={() => askRemove(s)}>Remove</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
